import AdminRequired from 'admin/adminRequired'
import Permission from 'admin/permissionRequired'
import { User } from 'auth/state'
import classNames from 'classnames'
import Feature from 'features/feature'
import { LoggingService } from 'http/loggingService'
import * as React from 'react'
import { useSelector } from 'react-redux'
import MenuItem from 'sidebar/sidebarMenuItem'
import { isSidebarOpen } from './selectors'
import { RELEASED_BADGE } from 'releaseNotes/htmlComponents'
import { ResponsiveImage } from 'uiComponents/image'
import conviousPinkLogo from 'images/convious-logo-pink.png'
import './styles.scss'
import { usePulseAccess } from 'pulse/hooks/usePulseAccess'
import { useTranslation } from 'translations'

interface SidebarProps {
    accountSlug: string
    hasPermission: (permission: string, accountSlug: string) => boolean
    hasFeature: (feature: string, accountSlug?: string) => boolean
    loggingService: LoggingService
    user: User | null
}

export function SidebarMenu(props: Readonly<SidebarProps>) {
    const { accountSlug, hasPermission, loggingService, user } = props
    const { t } = useTranslation()
    const showProducts =
        (hasPermission('view_pricing_settings', accountSlug) || hasPermission('view_product_validity', accountSlug)) &&
        !props.user?.resellerId
    const isReseller = !!props.user?.resellerId
    const pulseEnabled = usePulseAccess(accountSlug)
    const [openItem, setOpenItem] = React.useState<string>('')
    const open = useSelector(isSidebarOpen)

    function logClick(path: string) {
        if (path === 'bug_report') {
            loggingService.logAction('bug_report')
        } else {
            loggingService.logAction('navigation', {
                click_to: path,
                source: 'sidebar',
            })
        }
    }

    function clickChildless(path: string) {
        logClick(path)
        setOpenItem(path)
    }

    return (
        <div
            className={classNames('sidebar-wrapper', {
                'with-impersonation-banner': !!user?.impersonatedBy,
                open: open,
            })}
        >
            <div className="sidebar-logo">
                <ResponsiveImage className="convious-logo" src={conviousPinkLogo} alt="Convious" />
            </div>
            <nav className="main-navigation" aria-label="Main navigation">
                <ul className="menu">
                    <MenuItem
                        path={`/account/${accountSlug}/dashboard/overview/`}
                        text={pulseEnabled ? t('sidebar.menu.pulse') : t('sidebar.menu.dashboard')}
                        onClick={clickChildless}
                        iconName={pulseEnabled ? 'Activity' : 'Home'}
                    />
                    <Permission name="access_orders" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/orders/`}
                            text={t('sidebar.menu.orders')}
                            iconName="ShoppingBag"
                            onClick={clickChildless}
                        />
                    </Permission>
                    {showProducts && (
                        <MenuItem
                            path={`/account/${accountSlug}/products`}
                            text={t('sidebar.menu.products')}
                            iconName="Tickets"
                            onClick={clickChildless}
                        />
                    )}
                    {!isReseller && (
                        <MenuItem
                            path={`/account/${accountSlug}/venue/`}
                            text={t('sidebar.menu.venue')}
                            iconName="MapPinned"
                            onClick={clickChildless}
                        />
                    )}
                    {!isReseller && (
                        <MenuItem
                            path={`/account/${accountSlug}/pos/`}
                            text={t('sidebar.menu.pointOfSale')}
                            iconName="Monitor"
                            onClick={clickChildless}
                        />
                    )}
                    {!isReseller && (
                        <Feature name="memberships" accountSlug={accountSlug}>
                            <Permission name="access_orders" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/memberships/`}
                                    className="memberships"
                                    text={t('sidebar.menu.memberships')}
                                    iconName="IdCard"
                                    onClick={logClick}
                                />
                            </Permission>
                        </Feature>
                    )}
                    <Feature name="pricingMenu" accountSlug={accountSlug}>
                        <Permission name="partner_admin" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/global_pricing_settings`}
                                text={t('sidebar.menu.dynamicPricing')}
                                iconName="Sparkles"
                                onClick={logClick}
                            />
                        </Permission>
                    </Feature>
                    <Feature name="discountCodes" accountSlug={accountSlug}>
                        <Permission name="access_codes" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/discounts`}
                                className="discounts"
                                text={t('sidebar.menu.discounts')}
                                iconName="BadgePercent"
                                onClick={logClick}
                            />
                        </Permission>
                    </Feature>
                    <Permission name="view_wonderbar" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/engage/`}
                            text={t('sidebar.menu.engage')}
                            iconName="RadioTower"
                            onClick={clickChildless}
                        />
                    </Permission>
                    <Permission name="access_analytics" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/reports/`}
                            text={t('sidebar.menu.reports')}
                            iconName="ChartLine"
                            userpilot="menu-sales"
                            onClick={clickChildless}
                        />
                    </Permission>
                    {!isReseller && (
                        <Feature name="release_notes_page" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/release_notes/`}
                                text={t('sidebar.menu.whatsNew')}
                                onClick={logClick}
                                iconName="Mailbox"
                                rightComponent={<div dangerouslySetInnerHTML={{ __html: RELEASED_BADGE }} />}
                            />
                        </Feature>
                    )}
                    <Permission name="partner_admin" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/settings`}
                            text={t('sidebar.menu.settings')}
                            iconName="Settings2"
                            onClick={clickChildless}
                        />
                    </Permission>
                    <AdminRequired>
                        <MenuItem
                            path="/admin/"
                            text={t('sidebar.menu.admin')}
                            iconName="Settings"
                            onClick={setOpenItem}
                            close={openItem !== '/admin/'}
                        >
                            <MenuItem path="/admin/onboarding/" text={t('sidebar.menu.onboarding')} onClick={logClick} />
                            <Permission name="fund_transfers" accountSlug={accountSlug}>
                                <MenuItem path="/admin/fund_transfer/" text={t('sidebar.menu.fundTransfer')} onClick={logClick} />
                            </Permission>
                        </MenuItem>
                    </AdminRequired>
                </ul>
            </nav>
            <div className="app-version" data-version={process.env.VERSION} title={process.env.VERSION}>
                {process.env.VERSION}
            </div>
        </div>
    )
}
